import React, { useState, useEffect } from 'react';
import { Car, AlertCircle, Info, X, Loader2, Check, Store as StoreIcon } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const VehicleNameMappingManagement = () => {
  const [mappings, setMappings] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    make: '',
    makeDesc: '',
    model: '',
    modelDesc: ''
  });

  const isSuperAdmin = authService.isSuperAdmin();

  useEffect(() => {
    fetchStores();
    if (selectedStore) {
      fetchMappings(selectedStore);
    }
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchMappings = async (storeId) => {
    setError('');
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/admin/vehicle-name-mappings?storeId=${storeId}`,
        {
          headers: authService.getAuthHeaders()
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMappings(data);
      } else {
        setError('Failed to fetch vehicle name mappings');
      }
    } catch (err) {
      setError('Error fetching vehicle name mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreChange = (storeId) => {
    const id = storeId ? Number(storeId) : null;
    setSelectedStore(id);
    setOpenDialog(false);
    setEditingMapping(null);
    if (id) {
      fetchMappings(id);
    } else {
      setMappings([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStore) {
      setError('Please select a store first');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const url = editingMapping
        ? `${getApiBaseUrl()}/admin/vehicle-name-mappings/${editingMapping.id}`
        : `${getApiBaseUrl()}/admin/vehicle-name-mappings`;

      const method = editingMapping ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          storeIdFk: selectedStore
        })
      });

      if (response.ok) {
        await fetchMappings(selectedStore);
        handleCloseDialog();
        setSuccess(editingMapping ? 'Vehicle name mapping updated successfully' : 'Vehicle name mapping created successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save vehicle name mapping');
      }
    } catch (err) {
      setError('Error saving vehicle name mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (mapping) => {
    setError('');
    setSuccess('');
    setEditingMapping(mapping);
    setFormData({
      make: mapping.make,
      makeDesc: mapping.makeDesc || '',
      model: mapping.model,
      modelDesc: mapping.modelDesc || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    const mapping = mappings.find((m) => m.id === id);
    const label = mapping ? `"${[mapping.make, mapping.model].filter(Boolean).join(' ')}"` : 'this vehicle name mapping';
    const confirmed = await confirm({
      title: `Delete vehicle name mapping ${label}?`,
      message: `The vehicle name mapping ${label} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/vehicle-name-mappings/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        await fetchMappings(selectedStore);
        setSuccess('Vehicle name mapping deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete vehicle name mapping');
      }
    } catch (err) {
      setError('Error deleting vehicle name mapping');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMapping(null);
    setFormData({
      make: '',
      makeDesc: '',
      model: '',
      modelDesc: ''
    });
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Vehicle Name Mappings"
        description="Manage store-specific vehicle name mappings that translate raw make and model descriptions into standardized values"
      />

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800 leading-relaxed">
          These mappings are used when interpreting DMS or third-party vehicle data for a store. Select a store to view, add, edit, or delete make/model mappings.
        </div>
      </div>

      {success && (
        <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-4 h-4 text-green-600 mr-2" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Store Selection */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <StoreIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Store to Manage Mappings *
            </label>
            <select
              value={selectedStore || ''}
              onChange={(e) => handleStoreChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name || store.storeId}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mappings List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center gap-3">
          <h3 className="text-sm font-medium text-gray-900">
            {selectedStore
              ? `Vehicle Name Mappings for Store (${mappings.length})`
              : 'Select a store to view mappings'}
          </h3>
          {isSuperAdmin && selectedStore && (
            <AdminAddButton onClick={() => {
              setSuccess('');
              setError('');
              setOpenDialog(true);
            }} />
          )}
        </div>

        {!selectedStore ? (
          <div className="p-8 text-center text-gray-500">
            <StoreIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">Please select a store to view vehicle name mappings</p>
          </div>
        ) : loading ? (
          <AdminLoading />
        ) : mappings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Car className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">No vehicle name mappings found for this store</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Make
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Make Desc
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model Desc
                  </th>
                  {isSuperAdmin && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mappings.map((mapping) => (
                  <tr key={mapping.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{mapping.make}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{mapping.makeDesc}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{mapping.model}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{mapping.modelDesc}</td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-right">
                        <AdminRowActions>
                          <AdminActionButton variant="edit" onClick={() => handleEdit(mapping)} />
                          <AdminActionButton variant="delete" loading={deletingId === mapping.id} loadingLabel="Deleting…" onClick={() => handleDelete(mapping.id)} />
                        </AdminRowActions>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                {editingMapping ? 'Edit Vehicle Name Mapping' : 'Create Vehicle Name Mapping'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make *
                </label>
                <input
                  type="text"
                  required
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Toyota"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make Description
                </label>
                <input
                  type="text"
                  value={formData.makeDesc}
                  onChange={(e) => setFormData({ ...formData, makeDesc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Toyota Motor Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model *
                </label>
                <input
                  type="text"
                  required
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Camry"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Description
                </label>
                <input
                  type="text"
                  value={formData.modelDesc}
                  onChange={(e) => setFormData({ ...formData, modelDesc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Camry LE Sedan"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingMapping ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default VehicleNameMappingManagement;
